import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import AuthProviders, { oauthErrorMessage } from '../components/AuthProviders';
import { api, isDemoMode, BILLING_LIVE } from '../lib/api';

const PAID_PLANS = new Set(['pro', 'max', 'team']);

export default function Signup() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [emailStep, setEmailStep] = useState<'credentials' | 'verify'>('credentials');
  const [resendSeconds, setResendSeconds] = useState(0);
  const [error, setError] = useState<string | null>(() => oauthErrorMessage(params.get('oauth_error')));
  const [busy, setBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const plan = (params.get('plan') ?? '').toLowerCase();
  const returnTo = BILLING_LIVE && PAID_PLANS.has(plan) ? `/dashboard?checkout=${plan}` : '/dashboard';
  const failureTo = BILLING_LIVE && PAID_PLANS.has(plan) ? `/signup?plan=${plan}` : '/signup';

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;
    const timer = window.setTimeout(() => setResendSeconds((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [resendSeconds]);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setBusy(true);
    try {
      const pending = await api.signup(email.trim(), password);
      setEmail(pending.email);
      setCode('');
      setResendSeconds(pending.resendAfterSeconds);
      setEmailStep('verify');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Signup failed');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the six-digit code from your email.');
      return;
    }
    setBusy(true);
    try {
      await api.verifySignup(email, code);
      nav(returnTo);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  const resend = async (): Promise<void> => {
    if (busy || resendSeconds > 0) return;
    setError(null);
    setBusy(true);
    try {
      const pending = await api.signup(email, password);
      setCode('');
      setResendSeconds(pending.resendAfterSeconds);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not resend the code');
    } finally {
      setBusy(false);
    }
  };

  const changeEmail = (): void => {
    setEmailStep('credentials');
    setCode('');
    setPassword('');
    setResendSeconds(0);
    setError(null);
  };

  return (
    <>
      <Navbar />
      <div className="page auth-wrap">
        <section className="auth-card">
          <h1>Start building</h1>
          <p className="auth-lead">Create your account and get straight to the first build.</p>
          <AuthProviders returnTo={returnTo} failureTo={failureTo} onError={setError} />
          {error && <p className="form-error" role="alert">{error}</p>}
          <div className="auth-divider"><span>or</span></div>
          <button
            className="auth-email-toggle"
            type="button"
            aria-expanded={showEmail}
            onClick={() => setShowEmail((open) => !open)}
          >
            {showEmail ? 'Hide email signup' : 'Continue with email'}
          </button>
          {showEmail && emailStep === 'credentials' && (
            <form className="auth-email-form" onSubmit={submit}>
              <div className="field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="password">Password <span style={{ opacity: 0.6 }}>(min 8 chars)</span></label>
                <input
                  id="password"
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
              <button className="btn btn-primary auth-submit" type="submit" disabled={busy}>
                {busy ? 'Creating account…' : 'Create account with email'}
              </button>
            </form>
          )}
          {showEmail && emailStep === 'verify' && (
            <form className="auth-email-form auth-verify-form" onSubmit={verify}>
              <div className="auth-verify-intro" role="status">
                <strong>Check your email</strong>
                <span>We sent a six-digit code to {email}. It expires in 15 minutes.</span>
              </div>
              <div className="field">
                <label htmlFor="verification-code">Verification code</label>
                <input
                  id="verification-code"
                  className="auth-code-input"
                  type="text"
                  required
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  autoFocus
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </div>
              <button className="btn btn-primary auth-submit" type="submit" disabled={busy || code.length !== 6}>
                {busy ? 'Verifying…' : 'Verify and continue'}
              </button>
              <div className="auth-verify-actions">
                <button type="button" onClick={() => void resend()} disabled={busy || resendSeconds > 0}>
                  {resendSeconds > 0 ? `Resend in ${resendSeconds}s` : 'Resend code'}
                </button>
                <button type="button" onClick={changeEmail} disabled={busy}>Use another email</button>
              </div>
            </form>
          )}
          <p className="auth-alt">Already have one? <Link to="/login">Log in</Link></p>
          {isDemoMode() && (
            <p className="demo-hint">OFFLINE DEMO — every sign-in option opens the local demo.</p>
          )}
        </section>
      </div>
    </>
  );
}
